// src/pages/DashboardAlmuerzosSede.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const API_STATS = "https://garibcasinos.com/api/get_stats_almuerzos_sede_plato.php";

const todayYMD = () => new Date().toISOString().slice(0, 10);

const addDays = (ymd, days) => {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const SEDE_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "Hijuelas", label: "Hijuelas" },
  { value: "San Felipe", label: "San Felipe" },
  { value: "SIN_SEDE", label: "SIN_SEDE" },
];

export default function DashboardAlmuerzosSede() {
  const [desde, setDesde] = useState(() => addDays(todayYMD(), -7));
  const [hasta, setHasta] = useState(() => todayYMD());
  const [sede, setSede] = useState("");

  const [rows, setRows] = useState([]); // [{sede,tipo,plato,cantidad}]
  const [totales, setTotales] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const cargar = async () => {
    setLoading(true);
    setErr("");
    setMsg("");

    try {
      const resp = await axios.get(API_STATS, { params: { desde, hasta, sede } });

      if (resp.data?.ok) {
        const arr = Array.isArray(resp.data.rows) ? resp.data.rows : [];
        setRows(arr);
        setTotales(resp.data.totales_por_sede || {});
        if (!arr.length) setMsg("Sin datos para el rango seleccionado.");
      } else {
        setRows([]);
        setTotales({});
        setErr(resp.data?.error || "No se pudo cargar estadísticas.");
      }
    } catch (e) {
      setRows([]);
      setTotales({});
      setErr(e?.response?.data?.error || e?.message || "Error de red.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sedes = useMemo(() => {
    const set = new Set(rows.map((r) => String(r.sede || "SIN_SEDE")));
    const preferred = ["Hijuelas", "San Felipe", "SIN_SEDE"];
    const rest = Array.from(set).filter((s) => !preferred.includes(s)).sort();
    const ordered = preferred.filter((s) => set.has(s)).concat(rest);
    return ordered.length ? ordered : ["Hijuelas", "San Felipe", "SIN_SEDE"];
  }, [rows]);

  const totalGeneral = useMemo(() => {
    return Object.values(totales).reduce((a, b) => a + Number(b || 0), 0);
  }, [totales]);

  // TOP platos por total del rango (sumando sedes)
  const TOP_N = 12;

  const topPlatos = useMemo(() => {
    const agg = new Map(); // "tipo: plato" -> total
    for (const r of rows) {
      const tipo = String(r.tipo || "SIN_TIPO").trim();
      const plato = String(r.plato || "SIN_PLATO").trim();
      const key = `${tipo}: ${plato}`;
      const c = Number(r.cantidad || 0);
      agg.set(key, (agg.get(key) || 0) + c);
    }
    return Array.from(agg.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_N)
      .map(([k]) => k);
  }, [rows]);

  // matriz sede -> "tipo: plato" -> cantidad
  const matrix = useMemo(() => {
    const m = {};
    for (const s of sedes) m[s] = {};
    for (const r of rows) {
      const s = String(r.sede || "SIN_SEDE");
      const tipo = String(r.tipo || "SIN_TIPO").trim();
      const plato = String(r.plato || "SIN_PLATO").trim();
      const key = `${tipo}: ${plato}`;
      const c = Number(r.cantidad || 0);
      if (!m[s]) m[s] = {};
      m[s][key] = (m[s][key] || 0) + c;
    }
    return m;
  }, [rows, sedes]);

  const chartData = useMemo(() => {
    const labels = topPlatos.length ? topPlatos : ["(Sin datos)"];
    return {
      labels,
      datasets: sedes.map((s) => ({
        label: s,
        data: labels.map((k) => Number(matrix?.[s]?.[k] || 0)),
        stack: "stack1",
      })),
    };
  }, [topPlatos, sedes, matrix]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        title: { display: true, text: "Top platos consumidos (apilado por sede)" },
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true },
      },
    };
  }, []);

  return (
    <section className="section">
      <div className="container">
        <div
          className="is-flex is-justify-content-space-between is-align-items-flex-start"
          style={{ gap: 12, flexWrap: "wrap" }}
        >
          <div>
            <h1 className="title">Dashboard Almuerzos por Sede</h1>
            <p className="has-text-grey">
              Rango: <b>{desde}</b> a <b>{hasta}</b> · Total: <b>{totalGeneral}</b>
              {sede ? (
                <>
                  {" "}
                  · Sede: <b>{sede}</b>
                </>
              ) : null}
            </p>
          </div>

          <div className="box" style={{ padding: 12 }}>
            <div className="columns is-mobile" style={{ marginBottom: 0 }}>
              <div className="column">
                <label className="label">Desde</label>
                <input className="input" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
              </div>

              <div className="column">
                <label className="label">Hasta</label>
                <input className="input" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
              </div>

              <div className="column">
                <label className="label">Sede</label>
                <div className="select is-fullwidth">
                  <select value={sede} onChange={(e) => setSede(e.target.value)}>
                    {SEDE_OPTIONS.map((o) => (
                      <option key={o.value || "__all__"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="column is-narrow" style={{ display: "flex", alignItems: "flex-end" }}>
                <button className={`button is-primary ${loading ? "is-loading" : ""}`} type="button" onClick={cargar}>
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>

        {err && <div className="notification is-danger is-light">{err}</div>}
        {msg && <div className="notification is-info is-light">{msg}</div>}

        {/* KPIs por sede */}
        <div className="columns is-variable is-4">
          {sedes.map((s) => (
            <div className="column" key={s}>
              <div className="box">
                <div className="has-text-grey" style={{ fontWeight: 700, fontSize: 13 }}>
                  {s}
                </div>
                <div style={{ fontSize: 26, fontWeight: 900 }}>
                  {Number(totales?.[s] || 0)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico */}
        <div className="box">
          <div style={{ height: 460 }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
          <p className="help">Mostrando Top {TOP_N} combinaciones “tipo: plato” por total del rango.</p>
        </div>

        {/* Tabla detalle */}
        <div className="box">
          <h2 className="title is-5 mb-2">Detalle: platos por sede</h2>
          <div style={{ overflowX: "auto" }}>
            <table className="table is-striped is-hoverable is-fullwidth">
              <thead>
                <tr>
                  <th>Sede</th>
                  <th>Tipo</th>
                  <th>Plato</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="has-text-centered has-text-grey">
                      Sin datos
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={`${r.sede}-${r.tipo}-${r.plato}-${i}`}>
                      <td>{r.sede}</td>
                      <td>{r.tipo}</td>
                      <td>{r.plato}</td>
                      <td>{r.cantidad}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}